/* Public Customer Account API client. Never use an Admin API token here. */
(() => {
  const text = (key, values) => window.PlaygeniI18n?.text('account.' + key, values) || '';
  const addressFields = 'id firstName lastName company address1 address2 city zip territoryCode zoneCode country province phoneNumber formatted(withName:true)';
  const orderFields = 'id name processedAt cancelledAt financialStatus fulfillmentStatus statusPageUrl subtotal { amount currencyCode } totalShipping { amount currencyCode } totalTax { amount currencyCode } totalDuties { amount currencyCode } totalTip { amount currencyCode } totalRefunded { amount currencyCode } totalPrice { amount currencyCode }';
  const orderItemFields = 'id name title productType sku quantity variantId customAttributes { key value } group { id } image { url altText } totalPrice { amount currencyCode }';
  const queries = {
    profile: `query Profile { customer { id firstName lastName emailAddress { emailAddress marketingState } defaultAddress { id } } }`,
    addresses: `query Addresses($after:String) { customer { defaultAddress { id } addresses(first:50,after:$after) { nodes { ${addressFields} } pageInfo { hasNextPage endCursor } } } }`,
    orders: `query Orders($after:String) { customer { orders(first:10,after:$after,reverse:true,sortKey:PROCESSED_AT) { nodes { ${orderFields} lineItems(first:50) { nodes { ${orderItemFields} } pageInfo { hasNextPage endCursor } } } pageInfo { hasNextPage endCursor } } } }`,
    order: `query OrderDetails($id:ID!,$after:String) { order(id:$id) { ${orderFields} billingAddress { formatted(withName:true) } shippingAddress { formatted(withName:true) } lineItems(first:50,after:$after) { nodes { ${orderItemFields} } pageInfo { hasNextPage endCursor } } } }`,
    subscribe: 'mutation MarketingSubscribe { customerEmailMarketingSubscribe { emailAddress { marketingState } userErrors { field message } } }',
    unsubscribe: 'mutation MarketingUnsubscribe { customerEmailMarketingUnsubscribe { emailAddress { marketingState } userErrors { field message } } }',
    updateProfile: 'mutation ProfileUpdate($input:CustomerUpdateInput!) { customerUpdate(input:$input) { customer { id firstName lastName } userErrors { field message } } }',
    createAddress: `mutation AddressCreate($address:CustomerAddressInput!,$defaultAddress:Boolean) { customerAddressCreate(address:$address,defaultAddress:$defaultAddress) { customerAddress { ${addressFields} } userErrors { field message } } }`,
    updateAddress: `mutation AddressUpdate($addressId:ID!,$address:CustomerAddressInput,$defaultAddress:Boolean) { customerAddressUpdate(addressId:$addressId,address:$address,defaultAddress:$defaultAddress) { customerAddress { ${addressFields} } userErrors { field message } } }`,
    deleteAddress: 'mutation AddressDelete($addressId:ID!) { customerAddressDelete(addressId:$addressId) { deletedAddressId userErrors { field message } } }',
  };
  const base64 = bytes => btoa(String.fromCharCode(...new Uint8Array(bytes))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  const random = () => base64(crypto.getRandomValues(new Uint8Array(32)));
  const decode = text => Uint8Array.from(atob(text.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0));
  function create(config, dependencies = {}) {
    const fetcher = dependencies.fetch || window.fetch.bind(window);
    const navigate = dependencies.navigate || ((url, {replace = false} = {}) => {
      if (replace) window.location.replace(url);
      else window.location.assign(url);
    });
    const storage = dependencies.storage || window.sessionStorage;
    const sessionKey = `pg-account:${config.shopDomain}:${config.clientId}`;
    const pendingKey = sessionKey + ':pending';
    let discoveryPromise, refreshPromise, sessionVersion = 0;
    function clearSession() { sessionVersion++; storage.removeItem(sessionKey); }
    const read = key => { try { return JSON.parse(storage.getItem(key) || 'null'); } catch { storage.removeItem(key); return null; } };
    const signInError = () => Object.assign(new Error(text('sign_in_again_html')), {signInRequired:true});
    function secureURL(value) {
      const url = new URL(value);
      if (url.protocol !== 'https:' || url.username || url.password) throw new Error(text('connection_unavailable_html'));
      return url.href;
    }
    async function json(url, options = {}) {
      let response;
      try { response = await fetcher(url, {cache:'no-store',credentials:'omit',referrerPolicy:'no-referrer',...options}); }
      catch { throw Object.assign(new Error(text('connection_error_html')), {retryable:true}); }
      if (response.status === 401) { clearSession(); throw signInError(); }
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        if (body.error === 'invalid_grant') { clearSession(); throw signInError(); }
        throw Object.assign(new Error(text('request_error_html')), {retryable:response.status >= 500});
      }
      return response.json();
    }
    async function discover() {
      if (!config.clientId || !config.shopDomain) throw new Error(text('sign_in_unavailable_html'));
      if (!discoveryPromise) discoveryPromise = (async () => {
        const origin = secureURL(`https://${config.shopDomain}`);
        const [auth, api] = await Promise.all([
          json(new URL('/.well-known/openid-configuration', origin).href),
          json(new URL('/.well-known/customer-account-api', origin).href),
        ]);
        for (const key of ['authorization_endpoint','token_endpoint','end_session_endpoint','issuer']) secureURL(auth[key]);
        const graphql = new URL(secureURL(api.graphql_api));
        // Pin the schema used by the queries instead of silently following latest.
        graphql.pathname = graphql.pathname.replace(/\/api\/[^/]+\/graphql\/?$/, '/api/2026-07/graphql');
        return {auth, graphql:graphql.href};
      })().catch(error => { discoveryPromise = null; throw error; });
      return discoveryPromise;
    }
    async function login(email = '') {
      // The custom workspace requires a registered public OAuth client. Until
      // it is configured, use Shopify's existing account sign-in and orders.
      if (!config.clientId) {
        let url;
        try { url = new URL(config.hostedLoginUrl || '', window.location.origin); }
        catch { throw new Error(text('sign_in_unavailable_html')); }
        if (!config.hostedLoginUrl || url.origin !== window.location.origin || url.username || url.password ||
            !/\/(?:customer_authentication\/login|account\/login)\/?$/.test(url.pathname)) {
          throw new Error(text('sign_in_unavailable_html'));
        }
        if (url.pathname.endsWith('/customer_authentication/login')) {
          url.searchParams.set('return_to', (config.storeRoot || '/') + 'account');
        }
        if (email) url.searchParams.set('login_hint', email);
        navigate(url.href, {replace:true});
        return;
      }
      const {auth} = await discover();
      const verifier = random(), state = random(), nonce = random();
      const callbackUrl = secureURL(config.callbackUrl);
      if (new URL(callbackUrl).origin !== window.location.origin) throw new Error(text('preview_sign_in_unavailable_html'));
      const challenge = base64(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier)));
      const returnHash = /^#(?:orders|details|order\/.*)$/.test(window.location.hash) ? window.location.hash : '';
      storage.setItem(pendingKey, JSON.stringify({verifier,state,nonce,callbackUrl,returnHash,createdAt:Date.now()}));
      const url = new URL(auth.authorization_endpoint);
      const params = {client_id:config.clientId,redirect_uri:callbackUrl,response_type:'code',scope:'openid email customer-account-api:full',state,nonce,code_challenge:challenge,code_challenge_method:'S256'};
      if (email) params.login_hint = email;
      if (config.locale) params.locale = config.locale;
      Object.entries(params).forEach(([key,value]) => url.searchParams.set(key,value));
      // Back should return to the storefront, not an automatic redirect loop.
      navigate(url.href, {replace:true});
    }
    function validateTokenResponseIdentity(token, nonce, auth) {
      // Only called for the ID token obtained directly from the HTTPS token endpoint.
      // OIDC Core 3.1.3.7(6) allows TLS issuer validation here. Shopify's JWKS
      // endpoint has no browser CORS support. Never accept an ID token from a URL.
      const parts = String(token || '').split('.');
      if (parts.length !== 3 || !parts[2]) throw signInError();
      let header, claims;
      try { header = JSON.parse(new TextDecoder().decode(decode(parts[0]))); claims = JSON.parse(new TextDecoder().decode(decode(parts[1]))); }
      catch { throw signInError(); }
      const audience = Array.isArray(claims.aud) ? claims.aud : [claims.aud];
      if (header.alg !== 'RS256' || claims.iss !== auth.issuer || audience.length !== 1 || audience[0] !== config.clientId ||
          (claims.azp && claims.azp !== config.clientId) || claims.nonce !== nonce || !claims.sub ||
          !Number.isFinite(claims.iat) || claims.iat * 1000 > Date.now() + 60000 ||
          !Number.isFinite(claims.exp) || claims.exp * 1000 <= Date.now()) throw signInError();
    }
    async function callback(search) {
      const current = sessionVersion;
      const params = new URLSearchParams(search), pending = read(pendingKey);
      storage.removeItem(pendingKey);
      if (params.has('error')) throw new Error(text('sign_in_canceled_html'));
      if (!pending || Date.now()-pending.createdAt > 600000 || params.get('state') !== pending.state || !params.get('code')) throw signInError();
      const {auth} = await discover();
      const tokens = await json(auth.token_endpoint, {method:'POST',redirect:'error',headers:{'Content-Type':'application/x-www-form-urlencoded'},body:new URLSearchParams({grant_type:'authorization_code',client_id:config.clientId,redirect_uri:pending.callbackUrl,code:params.get('code'),code_verifier:pending.verifier}).toString()});
      validateTokenResponseIdentity(tokens.id_token, pending.nonce, auth);
      if (!tokens.access_token || !(Number(tokens.expires_in)>0)) throw signInError();
      if (current !== sessionVersion) throw signInError();
      storage.setItem(sessionKey, JSON.stringify({...tokens,expiresAt:Date.now()+Number(tokens.expires_in)*1000}));
      return pending.returnHash || '';
    }
    async function accessToken() {
      const current = sessionVersion;
      const session = read(sessionKey);
      if (!session?.access_token) throw signInError();
      if (session.expiresAt > Date.now()+60000) return session.access_token;
      if (!session.refresh_token) { clearSession(); throw signInError(); }
      if (!refreshPromise) refreshPromise = (async () => {
        const {auth} = await discover();
        const tokens = await json(auth.token_endpoint, {method:'POST',redirect:'error',headers:{'Content-Type':'application/x-www-form-urlencoded'},body:new URLSearchParams({grant_type:'refresh_token',client_id:config.clientId,refresh_token:session.refresh_token}).toString()});
        if (!tokens.access_token || !(Number(tokens.expires_in)>0)) throw signInError();
        if (current !== sessionVersion) throw signInError();
        storage.setItem(sessionKey,JSON.stringify({...session,...tokens,expiresAt:Date.now()+Number(tokens.expires_in)*1000}));
        return tokens.access_token;
      })().catch(error => { if (error.signInRequired && current === sessionVersion) clearSession(); throw error; }).finally(() => { refreshPromise = null; });
      return refreshPromise;
    }
    async function request(query, variables = {}) {
      const current = sessionVersion;
      const token = await accessToken();
      const {graphql} = await discover();
      if (current !== sessionVersion) throw signInError();
      const options = {method:'POST',headers:{'Content-Type':'application/json',Authorization:token},body:JSON.stringify({query,variables})};
      let result;
      try { result = await json(graphql, options); }
      catch (error) {
        // A lost write response can still mean the write succeeded. Replay only reads.
        if (!error.retryable || !/^query\b/.test(query)) throw error;
        await new Promise(resolve => setTimeout(resolve, 300));
        if (current !== sessionVersion) throw signInError();
        result = await json(graphql, options);
      }
      if (current !== sessionVersion) throw signInError();
      if (result.errors?.length) throw new Error(text('load_save_error_html'));
      if (result.data?.customer === null) { clearSession(); throw signInError(); }
      const errors = Object.values(result.data || {}).flatMap(value => value?.userErrors || []);
      if (errors.length) throw Object.assign(new Error(errors.map(error => error.message).join(' ')), {fields:errors});
      if (!result.data) throw new Error(text('load_error_html'));
      return result.data;
    }
    async function logout() {
      const session = read(sessionKey);
      clearSession();storage.removeItem(pendingKey);
      const {auth} = await discover();
      const url = new URL(auth.end_session_endpoint);
      if (session?.id_token) url.searchParams.set('id_token_hint',session.id_token);
      url.searchParams.set('post_logout_redirect_uri',secureURL(new URL(config.storeRoot || '/', window.location.origin).href));
      navigate(url.href);
    }
    // The current catalog's plain books have no plans or purchase configuration.
    // Storymakers, memberships, bundles and personalized lines need fresh choices.
    function canReorder(order) {
      const lines = order.lineItems?.nodes;
      return Boolean(lines?.length && !order.lineItems.pageInfo?.hasNextPage && lines.every(line =>
        line.productType === 'Book' && Array.isArray(line.customAttributes) && !line.customAttributes.length && line.group === null &&
        /^gid:\/\/shopify\/ProductVariant\/\d+$/.test(line.variantId || '') && Number.isInteger(line.quantity) && line.quantity > 0));
    }
    return {
      sessionKey, login, callback, logout, canReorder,
      isSignedIn:() => Boolean(read(sessionKey)?.access_token),
      profile:async () => (await request(queries.profile)).customer,
      addresses:async (after = null) => (await request(queries.addresses,{after})).customer,
      orders:async (after = null) => (await request(queries.orders,{after})).customer.orders,
      order:async (id, after = null) => {
        const result = (await request(queries.order,{id,after})).order;
        if (!result) throw new Error(text('order_unavailable_html'));
        return result;
      },
      setMarketing:async subscribed => {
        const result = await request(subscribed ? queries.subscribe : queries.unsubscribe);
        return result[subscribed ? 'customerEmailMarketingSubscribe' : 'customerEmailMarketingUnsubscribe'].emailAddress;
      },
      reorder:async order => {
        const lines = order.lineItems?.nodes;
        if (!canReorder(order)) throw new Error(text('reorder_error_html'));
        const base = new URL(config.storeRoot || '/', window.location.origin);
        if (base.origin !== window.location.origin) throw new Error(text('cart_unavailable_html'));
        let response;
        try { response = await fetcher(new URL('cart/add.js',base).href, {method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json'},body:JSON.stringify({items:lines.map(line=>({id:line.variantId.split('/').pop(),quantity:line.quantity}))})}); }
        catch { throw new Error(text('cart_update_error_html')); }
        if (!response.ok) throw new Error(text('items_unavailable_html'));
        navigate(new URL('cart',base).href);
      },
      updateProfile:input => request(queries.updateProfile,{input:{firstName:input.firstName,lastName:input.lastName}}),
      saveAddress:(addressId,address,defaultAddress) => request(addressId ? queries.updateAddress : queries.createAddress, {...(addressId ? {addressId} : {}),address,defaultAddress}),
      deleteAddress:addressId => request(queries.deleteAddress,{addressId}),
    };
  }
  window.PGAccountAPI = {create};
})();
